import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Save, Plus, X, CheckCircle2, AlertCircle, Flag, Shield, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROGRAM_VISUALS = {
  flag_football: { icon: Flag, accent: 'bg-blue-500' },
  tackle_football: { icon: Shield, accent: 'bg-navy' },
  cheer: { icon: Sparkles, accent: 'bg-red' },
};

const AdminProgramConfigPage = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');
  const [successKey, setSuccessKey] = useState(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_URL}/api/program-configs`, { withCredentials: true });
      setConfigs(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load program configs');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, field, value) => {
    setConfigs(prev => prev.map(c => c.program_key === key ? { ...c, [field]: value } : c));
  };

  const updateIncludeItem = (key, idx, value) => {
    setConfigs(prev => prev.map(c => {
      if (c.program_key !== key) return c;
      const next = [...(c.includes || [])];
      next[idx] = value;
      return { ...c, includes: next };
    }));
  };

  const addIncludeItem = (key) => {
    setConfigs(prev => prev.map(c => c.program_key === key ? { ...c, includes: [...(c.includes || []), ''] } : c));
  };

  const removeIncludeItem = (key, idx) => {
    setConfigs(prev => prev.map(c => {
      if (c.program_key !== key) return c;
      const next = [...(c.includes || [])];
      next.splice(idx, 1);
      return { ...c, includes: next };
    }));
  };

  const handleSave = async (cfg) => {
    if (!window.confirm(`Save changes to ${cfg.name}? These will update what parents see and pay immediately.`)) {
      return;
    }
    setSavingKey(cfg.program_key);
    setError('');
    setSuccessKey(null);
    try {
      const payload = {
        name: cfg.name,
        description: cfg.description,
        standard_price: Number(cfg.standard_price),
        early_bird_price: Number(cfg.early_bird_price),
        sibling_price: Number(cfg.sibling_price),
        early_bird_deadline: cfg.early_bird_deadline,
        includes: (cfg.includes || []).map(s => String(s).trim()).filter(Boolean),
        active: !!cfg.active,
      };
      const r = await axios.put(`${API_URL}/api/program-configs/${cfg.program_key}`, payload, { withCredentials: true });
      setConfigs(prev => prev.map(c => c.program_key === cfg.program_key ? r.data : c));
      setSuccessKey(cfg.program_key);
      setTimeout(() => setSuccessKey(null), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <ParentLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="admin-program-config-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-display text-2xl font-black text-navy uppercase tracking-tight">
                Registration Program Configuration
              </h1>
              <p className="text-sm text-gray-600">
                Update pricing, early-bird deadline, and what each registration includes.
                Changes apply immediately across the registration flow and the parent FAQ.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2" data-testid="config-error">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : configs.length === 0 ? (
          <p className="text-gray-500 text-sm">No program configs found.</p>
        ) : (
          configs.map(cfg => {
            const visuals = PROGRAM_VISUALS[cfg.program_key] || {};
            const Icon = visuals.icon;
            return (
              <Card key={cfg.program_key} data-testid={`program-card-${cfg.program_key}`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="flex items-center space-x-3">
                    {Icon && (
                      <div className={`w-10 h-10 rounded-full ${visuals.accent} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <span>{cfg.name}</span>
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    <Label className="text-xs text-gray-600">Active</Label>
                    <Switch
                      checked={!!cfg.active}
                      onCheckedChange={(v) => updateField(cfg.program_key, 'active', v)}
                      data-testid={`active-toggle-${cfg.program_key}`}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`name-${cfg.program_key}`}>Program Name</Label>
                      <Input
                        id={`name-${cfg.program_key}`}
                        value={cfg.name || ''}
                        onChange={(e) => updateField(cfg.program_key, 'name', e.target.value)}
                        data-testid={`name-input-${cfg.program_key}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`deadline-${cfg.program_key}`}>Early Bird Deadline</Label>
                      <Input
                        id={`deadline-${cfg.program_key}`}
                        type="date"
                        value={cfg.early_bird_deadline || ''}
                        onChange={(e) => updateField(cfg.program_key, 'early_bird_deadline', e.target.value)}
                        data-testid={`deadline-input-${cfg.program_key}`}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor={`desc-${cfg.program_key}`}>Description</Label>
                    <Textarea
                      id={`desc-${cfg.program_key}`}
                      value={cfg.description || ''}
                      onChange={(e) => updateField(cfg.program_key, 'description', e.target.value)}
                      rows={2}
                      data-testid={`desc-input-${cfg.program_key}`}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor={`std-${cfg.program_key}`}>Standard Price ($)</Label>
                      <Input
                        id={`std-${cfg.program_key}`}
                        type="number"
                        min="0"
                        step="1"
                        value={cfg.standard_price ?? ''}
                        onChange={(e) => updateField(cfg.program_key, 'standard_price', e.target.value)}
                        data-testid={`std-input-${cfg.program_key}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`eb-${cfg.program_key}`}>Early Bird Price ($)</Label>
                      <Input
                        id={`eb-${cfg.program_key}`}
                        type="number"
                        min="0"
                        step="1"
                        value={cfg.early_bird_price ?? ''}
                        onChange={(e) => updateField(cfg.program_key, 'early_bird_price', e.target.value)}
                        data-testid={`eb-input-${cfg.program_key}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`sib-${cfg.program_key}`}>Sibling Price ($)</Label>
                      <Input
                        id={`sib-${cfg.program_key}`}
                        type="number"
                        min="0"
                        step="1"
                        value={cfg.sibling_price ?? ''}
                        onChange={(e) => updateField(cfg.program_key, 'sibling_price', e.target.value)}
                        data-testid={`sib-input-${cfg.program_key}`}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Registration Includes</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addIncludeItem(cfg.program_key)}
                        data-testid={`add-include-${cfg.program_key}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add item
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(cfg.includes || []).map((item, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <Input
                            value={item}
                            onChange={(e) => updateIncludeItem(cfg.program_key, idx, e.target.value)}
                            placeholder="e.g. 2 uniforms (home and away)"
                            data-testid={`include-item-${cfg.program_key}-${idx}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeIncludeItem(cfg.program_key, idx)}
                            data-testid={`remove-include-${cfg.program_key}-${idx}`}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      {(!cfg.includes || cfg.includes.length === 0) && (
                        <p className="text-xs text-gray-400">No items yet.</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    {successKey === cfg.program_key ? (
                      <span className="flex items-center text-sm text-green-700" data-testid={`save-success-${cfg.program_key}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Saved
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {cfg.updated_at ? `Last updated ${new Date(cfg.updated_at).toLocaleString()}` : ''}
                      </span>
                    )}
                    <Button
                      onClick={() => handleSave(cfg)}
                      disabled={savingKey === cfg.program_key}
                      className="bg-navy hover:bg-navy/90 text-white"
                      data-testid={`save-btn-${cfg.program_key}`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingKey === cfg.program_key ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminProgramConfigPage;
